import { FormikErrors, FormikValues, useFormik } from 'formik';
import {
  confirmPaymentAsync,
  selectConfirmPayment,
  selectDataRequired,
  selectLanguage,
  selectPaymentData,
  setInstalmentSelected,
  setLoadingConfirm,
} from 'features/payment/payment-slice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import Field from 'components/common/field';
import { Input, Instalment, PaymentMethod } from 'features/payment/interfaces';
import styles from './styles.module.scss';
import { InputTypes, PaymentMethodTypes } from '../../constants';
import { useState } from 'react';
import SmartField, { DLocalInstance, SmartFieldError } from './smart-fields';
import Bugsnag from '@bugsnag/browser';
import { FieldError } from 'hooks/use-smart-field';

interface Values {
  [key: string]: string;
}

const createInitialValues = (inputs: Input[], defaultValues: Values) => {
  const initialValues: Values = {};
  inputs.forEach(input => {
    // Preload element when input is 'COMBO_BOX' and only has one option.
    const val = InputTypes.COMBO_BOX === input.type && input.values?.length === 1 ? input.values[0].value : '';
    initialValues[input.name] = (defaultValues && defaultValues[input.name]) || input.defaultValue || val;
  });
  return initialValues;
};

const DataRequired = ({ paymentMethodType }: { paymentMethodType?: PaymentMethodTypes }) => {
  const { t } = useTranslation();
  const language = useAppSelector(selectLanguage);
  const { inputs, errorFields, instalmentSelected } = useAppSelector(selectDataRequired);
  const { loading } = useAppSelector(selectConfirmPayment);
  const paymentData = useAppSelector(selectPaymentData);

  const [dlocalInstance, setDlocalInstance] = useState<DLocalInstance>();
  const [cardInstance, setCardInstance] = useState();
  const [brand, setBrand] = useState('');
  const [cardError, setCardError] = useState<SmartFieldError>({ pan: '', expiration: '', cvv: '' });

  const dispatch = useAppDispatch();

  const getPaymentMethod = (brand: string, type: string): PaymentMethod | undefined => {
    const debitBrands: { [key: string]: string } = { VI: 'VD', MC: 'MD', CL: 'CD' };
    const debitBrand = type === 'DEBIT' && debitBrands[brand];

    let debitResult = undefined;
    let CCResult = undefined;
    const pms =
      (paymentData?.paymentMethodsGrouped && paymentData.paymentMethodsGrouped[PaymentMethodTypes.CREDIT_CARD]) || [];
    pms?.forEach(pm => {
      if (pm.code === debitBrand) debitResult = pm;
      if (pm.code === brand) CCResult = pm;
    });
    return debitResult || CCResult;
  };

  const handleCreditCardSubmit = (values: Values) => {
    dispatch(setLoadingConfirm(true));

    const valuesToSend: Values = {};
    Object.keys(values).forEach(key => {
      if (typeof values[key] === 'string') {
        if (values[key].trim() !== '') {
          valuesToSend[key] = values[key];
        }
      } else {
        valuesToSend[key] = values[key];
      }
    });

    const cardHolderName = `${valuesToSend.clientFirstName} ${valuesToSend.clientLastName}`;
    dlocalInstance
      ?.createToken(cardInstance, {
        name: cardHolderName,
      })
      .then(async result => {
        const { brand, type, bin } = await dlocalInstance.getBinInformation(cardInstance);
        dispatch(setLoadingConfirm(false));
        dispatch(
          confirmPaymentAsync({
            values: {
              ...valuesToSend,
              paymentToken: result.token,
              installmentsId: instalmentSelected?.id || '',
              installments: `${instalmentSelected?.installments || 1}`,
              totalInstallmentsAmount: instalmentSelected?.installmentAmount || '',
              totalAmount: instalmentSelected?.totalAmount || '',
              installmentsByBank: `${instalmentSelected?.installmentsByBank || ''}`,
            },
            paymentMethod: getPaymentMethod(brand, type),
            extraData: { bin },
          })
        );
      })
      .catch((result: { error: FieldError }) => {
        dispatch(setLoadingConfirm(false));

        if (result.error) {
          switch (result.error.param) {
            case 'number':
              setCardError({ ...cardError, pan: result?.error?.message || '' });
              break;
            case 'expiration':
              setCardError({ ...cardError, expiration: result?.error?.message || '' });
              break;
            case 'cvv':
              setCardError({ ...cardError, cvv: result?.error?.message || '' });
              break;
          }
        }

        Bugsnag.notify(new Error(`Create dlocal token error: ${result?.error?.message || 'Without message.'}`));
      });
  };

  const validate = (values: FormikValues) => {
    let errors: FormikErrors<FormikValues> = {};
    if (inputs) {
      inputs.forEach(input => {
        if (!values[input.name]) {
          errors[input.name] = t('dataRequired.required');
        } else if (errorFields && errorFields[input.name] === values[input.name]) {
          errors[input.name] = t('dataRequired.invalid');
        } else if (input.regex) {
          const regex = new RegExp(input.regex, 'g');
          if (!regex.test(values[input.name])) {
            errors[input.name] = t('dataRequired.invalid');
          }
        }
      });
    }
    return errors;
  };

  const getMask = (input: Input, inputs: Input[], values: FormikValues) => {
    if (input.name === 'clientDocument') {
      const options = inputs.find(i => i.name === 'clientDocumentType')?.values;
      const clientDocumentTypeSelected = values['clientDocumentType'];

      if (options?.length && clientDocumentTypeSelected) {
        return options.find(o => o.value === clientDocumentTypeSelected)?.mask;
      }
    }
    return input.mask;
  };

  const formik = useFormik({
    initialValues: createInitialValues(inputs || [], {}),
    validate,
    onSubmit: values => {
      if (paymentMethodType === PaymentMethodTypes.CREDIT_CARD) {
        handleCreditCardSubmit(values);
      } else {
        dispatch(confirmPaymentAsync({ values }));
      }
    },
  });

  return (
    <div>
      {formik && (
        <form onSubmit={formik.handleSubmit}>
          {paymentMethodType === PaymentMethodTypes.CREDIT_CARD && paymentData && (
            <SmartField
              disabled={loading}
              locale={language}
              country={paymentData?.country}
              currency={paymentData?.paymentInfo.currency}
              totalAmount={paymentData?.paymentInfo.total}
              installmentsId={instalmentSelected?.id || ''}
              error={cardError}
              onChangeError={(err: SmartFieldError) => setCardError(err)}
              onChangeBrand={(brand: string) => setBrand(brand)}
              onChangeInstallments={(option: Instalment | null) => dispatch(setInstalmentSelected(option))}
              onChangeSmartFieldData={({ dlocal, card }) => {
                setDlocalInstance(dlocal);
                setCardInstance(card);
              }}
            />
          )}
          <div className={styles.inputs_wrapper}>
            {inputs?.map((item, idx) => (
              <div
                key={idx.toString()}
                className={`${styles.field_wrapper} ${
                  idx % 2 == 0 ? styles.field_wrapper_left : styles.field_wrapper_right
                }`}
              >
                <Field
                  id={item.name}
                  type={item.type}
                  name={item.name}
                  label={t(`dataRequired.input.${item.name}`)}
                  value={formik.values[item.name]}
                  placeholder={item.prefill || t(`dataRequired.input.${item.name}`)}
                  prefix={item.prefix}
                  mask={getMask(item, inputs, formik.values)}
                  disabled={item.readOnly || loading}
                  options={item.values}
                  error={
                    (formik.touched[item.name] && formik.errors[item.name]) ||
                    (errorFields &&
                      errorFields[item.name] &&
                      errorFields[item.name] === formik.values[item.name] &&
                      t('dataRequired.invalid')) ||
                    ''
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            ))}
          </div>
          <button id="submit-data-required" type="submit" style={{ display: 'none' }} />
        </form>
      )}
    </div>
  );
};

export default DataRequired;
